import { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import _ from 'lodash'
import moment from 'moment'
import { Row, Col } from 'antd'
import { Form } from 'redux-form'
import { FormLoader, Tip, Toast, Button } from '@vzmi/shp_shared_compo'
import { safeAwait, httpHandler, fetchAllPages } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { ExcelExport, ExcelExportHelper } from '@vzmi/component_ec_nodejs_util'
import { fetchSeSaleStatsByDate, fetchSeSaleStats, fetchSeListingPageViews } from '../../../../actions'
import { formDecorator, Field } from '../../../Common/forms'
import { RangePickerField } from '../../../Common/Form'
import Block from '../../../Common/NewDashboard/Block/Block'
import FormRow from '../../../Common/NewDashboard/FormRow/FormRow'
import { StatsCard } from '../../../Dashboard/OperatingDashboard/StatsCard/StatsCard'
import { numeralFormat, getDefaultTimezoneMoment } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import { DATE_FORMAT, getUIFormatDate, getFormatData } from '../../../../lib/MomentHelper'
import { NoData } from '../../../Common/StatePage'
import { DEFAULT_DASHBOARD_DATE_RANGE, YESTERDAY } from '../../../../constants/date'
import { calendarDisableDate, getParamsListByMonth, SaleStatHelper } from '../../../Dashboard/OperatingDashboard/Shared'
import { computePeriodsParam } from '../../../Dashboard/OperatingDashboard/SaleStat'
import errorHelper from '../../../../../lib/errorHelper'
import { SELLER_DASHBOARD_ONLINE_DATE } from '../constants'
import { IconExportButton } from '../../../Common/ExportButton'
import withConnectedExport from '../../../../containers/Common/WithConnectedExport'
import SellerSaleDailyChart from './SellerSaleDailyChart'
import styles from './index.css'

const PAGE_VIEWS_API_LIMIT = 120

const DATE_RANGE_EXCEEDED_MESSAGE = '查詢區間最長為六個月'

export const FIELD = {
  MAIN_RANGE: 'mainRange'
}

export const CONFIG = {
  form: 'SELLER_SALE_STAT_FORM',
  initialValues: {
    [FIELD.MAIN_RANGE]: DEFAULT_DASHBOARD_DATE_RANGE
  }
}

const DEFAULT_CHART_PERIOD = {
  periodStart: getUIFormatDate(DEFAULT_DASHBOARD_DATE_RANGE[0]),
  periodEnd: getUIFormatDate(DEFAULT_DASHBOARD_DATE_RANGE[1])
}

const gaLabel = 's_saleoverview_downloadfile'

const validateDateRange = (formValues) => {
  const { periodStart, periodEnd } = getPeriodFromForm(formValues)
  if (moment(periodEnd, DATE_FORMAT.UI) >= calendarDisableDate(YESTERDAY, periodStart)) {
    return DATE_RANGE_EXCEEDED_MESSAGE
  }
}

const getPeriodFromForm = (formValues) => {
  const periodStart = getUIFormatDate(
    _.get(formValues, `${FIELD.MAIN_RANGE}[0]`) || DEFAULT_DASHBOARD_DATE_RANGE[0]
  )
  const periodEnd = getUIFormatDate(
    _.get(formValues, `${FIELD.MAIN_RANGE}[1]`) || DEFAULT_DASHBOARD_DATE_RANGE[1]
  )
  return { periodStart, periodEnd }
}

const useSaleStat = (actions) => {
  const { fetchSeSaleStatsByDate, fetchSeSaleStats, fetchSeListingPageViews } = actions
  const [loading, setLoading] = useState(false)
  const [saleStat, setSaleStat] = useState()

  const fetchMonthlyListingPageViews = useCallback(async (params) => {
    const [error, listingPageViews] = await safeAwait(
      fetchAllPages(params => {
        return fetchSeListingPageViews(params)
          .then(res => {
            if (res.error) {
              throw res
            }
            return res
          })
      }, {
        payload: params,
        selector: res => _.get(res, 'pageViews', [])
      })
    )
    if (error) {
      return Promise.reject(error)
    }

    return listingPageViews
  }, [fetchSeListingPageViews])

  const fetchAllListingPageViews = useCallback(async (params) => {
    const paramsWithLimit = {
      limit: PAGE_VIEWS_API_LIMIT,
      ...params
    }
    // API 限制日期區間最長一個月，因此跨月需要分開打
    const paramsList = getParamsListByMonth(paramsWithLimit)
    const [error, allListingPageViewsResp] = await safeAwait(
      Promise.all(
        paramsList.map(perMonthParams => fetchMonthlyListingPageViews({
          ...paramsWithLimit,
          ...perMonthParams
        }))
      )
    )

    if (error) {
      return Promise.reject(error)
    }

    // 將每個月的 pageView 加總
    const allListingPageViews = _.reduce(allListingPageViewsResp, (sumPageViews, resp) => {
      sumPageViews = sumPageViews.concat(resp.data)
      return sumPageViews
    }, [])

    const pagination = _.flow(
      _.head,
      firstResp => _.get(firstResp, 'pagination', {})
    )(allListingPageViewsResp)

    return { data: allListingPageViews, pagination }
  }, [fetchMonthlyListingPageViews])

  const fetchData = useCallback(async ({ periodStart, periodEnd }) => {
    setLoading(true)

    const periods = computePeriodsParam({ periodStart, periodEnd })
    const params = { period: [periods[0]] }

    const [error, payload] = await safeAwait(
      fetchSeSaleStats(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      Toast.error(errorMessage)
      setLoading(false)
      return
    }

    const stats = _.get(payload, 'stats[0]', {})
    const saleStatsData = _.get(stats, 'saleStats', [])

    const data = {
      data: saleStatsData
    }

    setLoading(false)
    setSaleStat(data)
  }, [fetchSeSaleStats])

  useEffect(() => {
    fetchData(DEFAULT_CHART_PERIOD)
  }, [fetchData])

  return {
    loading,
    saleStat,
    fetchData,
    fetchSeSaleStatsByDate,
    fetchAllListingPageViews
  }
}

const SaleCardList = ({ periodStat }) => {
  const data = _.get(periodStat, 'data', null)
  if (!data) {
    return <NoData iconType='history' />
  }
  const preloadedData = SaleStatHelper.prepareStatData(data)
  const listItems = !_.isEmpty(preloadedData)
    ? _.map(
      preloadedData,
      stat => {
        const { key, value } = stat
        const formatedVal = numeralFormat(value)
        const content = formatedVal
        return (
          <Col
            key={key}
            span={6}
          >
            <StatsCard
              title={SaleStatHelper.getTooltip(key)}
              titleContent={SaleStatHelper.getText(key)}
              content={content}
            />
          </Col>
        )
      })
    : <NoData iconType='history' />

  return (
    <Row
      data-role='saleStat-list'
      gutter={[12, 12]}
    >
      {listItems}
    </Row>
  )
}

const SaleStat = (props) => {
  const {
    actions,
    formValues,
    handleSubmit,
    openExportModal,
    closeExportModal,
    onGaClick
  } = props
  const { loading, saleStat, fetchData, fetchSeSaleStatsByDate, fetchAllListingPageViews } = useSaleStat(actions)

  const [chartPeriod, setChartPeriod] = useState(DEFAULT_CHART_PERIOD)

  const handleQuery = (formValues) => {
    const errorMessage = validateDateRange(formValues)
    if (errorMessage) {
      Toast.error(errorMessage)
      return
    }
    const { periodStart, periodEnd } = getPeriodFromForm(formValues)
    fetchData({ periodStart, periodEnd })
    setChartPeriod({ periodStart, periodEnd })
  }

  const exportExcel = async () => {
    if (onGaClick) {
      onGaClick(gaLabel)
    }
    const { fetchSeSaleStatsByDate } = actions
    openExportModal()

    const { periodStart, periodEnd } = getPeriodFromForm(formValues)
    const [
      reportDateStart, reportDateEnd
    ] = [periodStart, periodEnd].map(period =>
      getFormatData(moment(period, DATE_FORMAT.UI), DATE_FORMAT.API)
    )
    const fields = ['+newOrder']
    const params = { reportDateStart, reportDateEnd, fields }

    const [error, payload] = await safeAwait(
      fetchSeSaleStatsByDate(params).then(httpHandler)
    )

    if (error) {
      const errorMessage = errorHelper.parseErrorMessage(error)
      Toast.error(errorMessage)
      closeExportModal()
      return
    }

    const data = _.get(payload, 'newOrder.statistics', [])
    // 訂單成立金額轉數字
    const newData = _.map(data, item => {
      return {
        ...item,
        amount: _.toNumber(item.amount)
      }
    })

    const ExportHelper = new ExcelExportHelper({
      headers: {
        reportDate: '日期',
        amount: SaleStatHelper.getText('newOrderAmount'),
        quantity: SaleStatHelper.getText('newOrderCount')
      },
      fileName: 'seller_purchasingbyday_{{ nowDate.format("YYYYMMDDHHmmss") }}',
      sheetName: `${moment(periodStart, DATE_FORMAT.UI).format('YYYYMMDD')}-${moment(periodEnd, DATE_FORMAT.UI).format('YYYYMMDD')}`,
      bookType: 'xlsx'
    })

    const option = {
      fileName: ExportHelper.getFileName({
        nowDate: getDefaultTimezoneMoment()
      }),
      sheets: [
        {
          name: ExportHelper.getSheetName(),
          headers: ExportHelper.getSheetHeaders(),
          data: ExportHelper.convertDataByHeaders(newData)
        }
      ]
    }

    const ExcelExporter = new ExcelExport(option)
    ExcelExporter.download({ bookType: ExportHelper.getBookType() })
    closeExportModal()
  }
  const { periodStart } = getPeriodFromForm(formValues)

  const disableDateFilter = dateMoment => {
    return dateMoment < moment(SELLER_DASHBOARD_ONLINE_DATE, DATE_FORMAT.UI) ||
      dateMoment >= calendarDisableDate(YESTERDAY, periodStart)
  }

  return (
    <Form
      className={styles.saleStat}
      onSubmit={handleSubmit(handleQuery)}
    >
      <FormLoader isLoading={loading}>
        <Block>
          <FormRow
            label='日期區間'
            labelAlign='center'
          >
            <Field
              id={FIELD.MAIN_RANGE}
              key={FIELD.MAIN_RANGE}
              name={FIELD.MAIN_RANGE}
              data-role={FIELD.MAIN_RANGE}
              className={styles.mainRange}
              component={RangePickerField}
              disabledDate={disableDateFilter}
            />
          </FormRow>
          <FormRow
            labelAlign='top'
            className={styles.tips}
          >
            <Tip type='info'>
              {`可查詢最早日期為${SELLER_DASHBOARD_ONLINE_DATE}，起迄區間最長為六個月，每日中午更新`}
            </Tip>
          </FormRow>
          <FormRow labelAlign='top'>
            <Button
              className={styles.queryBtn}
              type='submit'
              size='large'
            >
              查詢
            </Button>
            <IconExportButton
              disabled={loading}
              data-role='seller-purchasing-daily-export-button'
              className={styles.exportButton}
              onClick={exportExcel}
              label='下載整筆檔案'
            />
          </FormRow>
        </Block>
        <SaleCardList periodStat={saleStat} />
        <SellerSaleDailyChart
          periodStart={chartPeriod.periodStart}
          periodEnd={chartPeriod.periodEnd}
          fetchSeSaleStatsByDate={fetchSeSaleStatsByDate}
          fetchAllListingPageViews={fetchAllListingPageViews}
        />
      </FormLoader>
    </Form>
  )
}

const mapStateToProps = state => ({
  formValues: _.get(state, `form.${CONFIG.form}.values`)
})

const mapDispatchToProps = (dispatch) => {
  const actions = {
    fetchSeSaleStatsByDate,
    fetchSeSaleStats,
    fetchSeListingPageViews
  }
  return {
    actions: bindActionCreators(actions, dispatch)
  }
}

const SaleStatWithDecorator = formDecorator(CONFIG)(SaleStat)

export default withConnectedExport(connect(
  mapStateToProps,
  mapDispatchToProps
)(SaleStatWithDecorator))
